import React, { lazy, Suspense, useEffect, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import ChunkLoadErrorBoundary from './components/ChunkLoadErrorBoundary';
import LoadingFallback from './components/LoadingFallback';
import Layout from './components/Layout';
import { AppProvider } from './context/AppContext';
import useAppState from './hooks/useAppState';
import useOptimizedAudio from './hooks/useOptimizedAudio';
import { ErrorInfo } from 'react';

const PhishingLogin = lazy(() => import('./components/PhishingLogin'));
const PhishingRegistration = lazy(
  () => import('./components/PhishingRegistration')
);
const PhishingOnboarding = lazy(
  () => import('./components/PhishingOnboarding')
);
import PhishingHero from './components/PhishingHero';
const PhishingSimulationsList = lazy(
  () => import('./components/PhishingSimulationsList')
);
const PhishingSimulationDetail = lazy(
  () => import('./components/PhishingSimulationDetail')
);
const PhishingCaregiverDashboard = lazy(
  () => import('./components/PhishingCaregiverDashboard')
);
const PhishingGuides = lazy(() => import('./components/PhishingGuides'));
const PhishingHelp = lazy(() => import('./components/PhishingHelp'));
const PhishingAboutUs = lazy(() => import('./components/PhishingAboutUs'));
const PhishingUserStatsDashboard = lazy(
  () => import('./components/PhishingUserStatsDashboard')
);
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const GrandpaProgressDashboard = lazy(
  () => import('./components/GrandpaProgressDashboard')
);
const ScamSimApp = lazy(() => import('./components/ScamSimApp'));
const EnhancedInteractiveSimulationDemo = lazy(
  () => import('./components/EnhancedInteractiveSimulationDemo')
);
const PhishingWebSimulationDemo = lazy(
  () => import('./components/PhishingWebSimulationDemo')
);
const SocialMediaScamSimulationDemo = lazy(
  () => import('./components/SocialMediaScamSimulationDemo')
);
const EnhancedRedFlagDemo = lazy(
  () => import('./components/EnhancedRedFlagDemo')
);

const WELCOME_AUDIO_KEY: string = 'welcomeAudioPlayedAt';
const ONE_DAY_MS: number = 24 * 60 * 60 * 1000;

interface ChunkErrorHandlerProps {
  error: Error;
  errorInfo: ErrorInfo;
  isChunkError: boolean;
}

const App: React.FC = () => {
  const { state, actions } = useAppState();

  // Optimized welcome audio with lazy loading
  const welcomeAudio = useOptimizedAudio({
    src: '/welcome.mp3',
    preload: 'none', // Only load when needed
    lazy: true,
    volume: 0.7,
  });

  // Play welcome audio only once per day if sound is enabled
  const playWelcomeAudio = useCallback(async (): Promise<void> => {
    try {
      // Only try to play audio if it's available and sound is enabled
      if (!state.soundEnabled) return;
      
      const lastPlayed: string | null = localStorage.getItem(WELCOME_AUDIO_KEY);
      const lastTimestamp: number = lastPlayed ? parseInt(lastPlayed, 10) : 0;
      if (lastPlayed && Date.now() - lastTimestamp <= ONE_DAY_MS) {
        return; // Already played recently
      }

      // Check if audio file exists before attempting to play
      const audioExists: boolean = await fetch('/welcome.mp3', { method: 'HEAD' })
        .then((response: Response) => response.ok)
        .catch(() => false);
      
      if (!audioExists) {
        // Audio file doesn't exist, skip gracefully
        return;
      }
      
      // Initialize audio if not ready and play
      if (!welcomeAudio.isReady) {
        welcomeAudio.initialize();
      }
      
      await welcomeAudio.play();
      localStorage.setItem(WELCOME_AUDIO_KEY, Date.now().toString());
    } catch (err) {
      // Always fail gracefully for audio - don't let it break the app
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Welcome audio playback failed:', err);
      }
    }
  }, [state.soundEnabled, welcomeAudio]);

  useEffect(() => {
    playWelcomeAudio();
  }, [playWelcomeAudio]);

  const handleChunkError = (error: Error, errorInfo: ErrorInfo, isChunkError: boolean): void => {
    // Log chunk errors for monitoring
    if (isChunkError && process.env.NODE_ENV === 'production') {
      // In production, you might want to send this to an error tracking service
      console.warn('Chunk loading failed:', error.message);
    }
  };

  return (
    <AppProvider value={{ state, actions }}>
      <Layout>
        <ChunkLoadErrorBoundary onError={handleChunkError}>
          <ErrorBoundary>
            <Suspense
              fallback={
                <LoadingFallback 
                  message="Cargando aplicación..."
                  timeout={30000}
                  showProgress={true}
                  showRetry={true}
                />
              }
            >
              <Routes>
                <Route path='/login' element={<PhishingLogin />} />
                <Route path='/registration' element={<PhishingRegistration />} />
                <Route path='/onboarding' element={<PhishingOnboarding />} />
                <Route path='/' element={<PhishingHero />} />
                <Route
                  path='/simulaciones'
                  element={<PhishingSimulationsList />}
                />
                <Route
                  path='/simulaciones/detalle'
                  element={<PhishingSimulationDetail />}
                />
                <Route path='/guias' element={<PhishingGuides />} />
                <Route path='/ayuda' element={<PhishingHelp />} />
                <Route path='/about' element={<PhishingAboutUs />} />
                <Route
                  path='/cuidador'
                  element={<PhishingCaregiverDashboard />}
                />
                <Route
                  path='/user-stats'
                  element={<PhishingUserStatsDashboard />}
                />
                <Route
                  path='/grandpa-progress'
                  element={<GrandpaProgressDashboard />}
                />
                <Route path='/admin' element={<AdminDashboard />} />
                <Route path='/profile' element={<UserProfile />} />
                <Route path='/scamsim' element={<ScamSimApp />} />
                <Route
                  path='/demo-enhanced'
                  element={<EnhancedInteractiveSimulationDemo />}
                />
                <Route
                  path='/demo-web-simulation'
                  element={<PhishingWebSimulationDemo />}
                />
                <Route
                  path='/demo-social-media'
                  element={<SocialMediaScamSimulationDemo />}
                />
                <Route
                  path='/demo-enhanced-redflag'
                  element={<EnhancedRedFlagDemo />}
                />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </ChunkLoadErrorBoundary>
      </Layout>
    </AppProvider>
  );
};

export default App;